import { StripePaymentElement } from '@/components/element/Form/StripePaymentElement';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useWorkspaceInvalidate } from '@/hooks/api/0101_workspace';
import { useUpdateNextPlan } from '@/hooks/api/2290_update_next_plan';
import { usePlans } from '@/hooks/api/2300_contract_plans';
import { Plan } from '@/hooks/api/models';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { storage } from '@/lib/storage';

export const EditNextPlan = () => {
  const { _w_, me } = useCurrentWorkspace();

  if (me?.admin) {
    return <_EditNextPlan />;
  } else {
    return <Navigate replace to={'/workspaces'} />;
  }
};

const _EditNextPlan = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('スペースプラン変更'), []);
  useEffect(() => setBreadcrumbs([{ label: `スペース設定`, path: `/w/${_w_!.identifier}/setting` }]), []);

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, me } = useCurrentWorkspace();

  const { data: plans } = usePlans();
  const { mutate: updateNextPlanMutate, data, isPending, error } = useUpdateNextPlan();
  const { invalidate: invalidateWorkspace } = useWorkspaceInvalidate(_w_!.identifier);
  const [selectedPlanId, setSelectedPlanId] = useState<number>(() => {
    const _id = storage.selectedPlanId.get();
    return _id ? _id : 2;
  });
  const [selectablePlans, setSelectablePlans] = useState<Plan[]>([]);
  const [paymentInfoUpdated, setPaymentInfoUpdated] = useState<boolean>(false);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (selectedPlanId) {
      storage.selectedPlanId.set(selectedPlanId);
    }
  }, [selectedPlanId]);

  useEffect(() => {
    if (plans) {
      if (_w_!.current_contracted_plan.plan.name == 'standard_annually') {
        setSelectedPlanId(_w_!.current_contracted_plan.plan.id);
      }

      setSelectablePlans(plans.filter((d) => d.name != 'personal'));
    }
  }, [plans]);

  useEffect(() => {
    if (paymentInfoUpdated) {
      updateNextPlanMutate({ identifier: _w_!.identifier, plan_id: selectedPlanId });
    }
  }, [paymentInfoUpdated]);

  useEffect(() => {
    if (data) {
      invalidateWorkspace();
      storage.selectedPlanId.clear();
      toastSuccess('プランの設定が完了しました');
      navigate(`/w/${_w_!.identifier}/setting`);
    }
  }, [data]);

  if (plans == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'basic'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-start">
                <div className="col-md-8">
                  <h5 className="mb-1">スペースプラン変更</h5>
                  <p className="text-xs mb-0">
                    現在の契約の期間が残っている場合、 次回契約更新から指定したプランに切り替わります。
                  </p>
                </div>
                <div className="col-md-4 d-flex align-items-top justify-content-end">
                  <Link to="https://www.biz-node.app/pricing" className="btn bg-gradient-secondary" target="_blank">
                    プランについて
                  </Link>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="form-group">
                  <label>プラン</label>
                  <select
                    className="form-control"
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(Number(e.target.value))}
                  >
                    {selectablePlans.map((d) => (
                      <option key={`plan-${d.id}`} value={d.id}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
                <StripePaymentElement setPaymentInfoUpdated={setPaymentInfoUpdated} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
